import { useQuery } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import WorksheetEditor, { WorksheetEditorFormData, WorksheetModification } from '@components/forms/WorksheetEditor';
import HandleQuery from '@components/HandleQuery';
import ExpertJourneyPage from '@components/pageTypes/ExpertJourneyPage';
import useCreateFileResource from '@hooks/mutations/useCreateFileResource';
import useCreateWorksheet from '@hooks/mutations/useCreateWorksheet';
import useMyClient from '@hooks/useMyClient';
import {
  GetJourneyWithPlanDocument,
  GetJourneyWithPlanQuery,
  GetJourneyWithPlanQueryVariables,
  GetWorksheetTemplateDocument,
  GetWorksheetTemplateQuery,
  GetWorksheetTemplateQueryVariables
} from '@typing/Generated';
import { ErrorSetter, handleMutationResponse } from '@utils/formUtils';
import { journeyRoute } from '@utils/routeUtils';

type Props = {
  journeyId: string;
  worksheetTemplateId: string;
};

const ExpertJourneyWorksheetTemplatePage = ({ journeyId, worksheetTemplateId }: Props) => {
  const [loading, setLoading] = useState(false);
  const intl = useIntl();
  const myClient = useMyClient();
  const history = useHistory();
  const createFileResource = useCreateFileResource(journeyId);

  const getJourneyWithPlanQuery = useQuery<GetJourneyWithPlanQuery, GetJourneyWithPlanQueryVariables>(
    GetJourneyWithPlanDocument,
    {
      variables: {
        id: journeyId
      }
    }
  );

  const createWorksheet = useCreateWorksheet(getJourneyWithPlanQuery.data?.journey);

  const getWorksheetTemplateQuery = useQuery<GetWorksheetTemplateQuery, GetWorksheetTemplateQueryVariables>(
    GetWorksheetTemplateDocument,
    {
      variables: {
        id: worksheetTemplateId
      }
    }
  );

  const onSave = useCallback(
    (
      modifications: WorksheetModification[],
      setBaseErrors: (errors: string[]) => void,
      setError: ErrorSetter<WorksheetEditorFormData>,
      _leavingLocation?: string,
      resetForm?: () => void
    ) =>
      createWorksheet({
        variables: {
          journeyId,
          worksheetModifications: modifications.map(modification => ({
            completed: modification.completed,
            details: modification.details,
            itemId: modification.item.id
          })),
          worksheetTemplateId
        }
      }).then(response => {
        handleMutationResponse<WorksheetEditorFormData>({
          data: response.data?.worksheetCreate,
          intl,
          onError: () => {
            setLoading(false);
          },
          onSuccess: () => {
            const worksheet = response.data?.worksheetCreate?.worksheet;
            if (worksheet) {
              createFileResource({
                variables: {
                  journeyId,
                  title: worksheet.title,
                  worksheetId: worksheet.id
                }
              }).then(data => {
                resetForm?.();
                if (data.data?.fileResourceCreate?.fileResource?.title) {
                  setLoading(false);
                  history.push(
                    journeyRoute({
                      journeyId,
                      linkAfterJourney: `/drive?newFileTitle=${data.data.fileResourceCreate.fileResource.title}`,
                      myClient
                    })
                  );
                }
              });
            }
          },
          setBaseErrors,
          setError,
          txBase: 'errors.worksheet'
        });
      }),
    [createFileResource, createWorksheet, history, intl, journeyId, myClient, worksheetTemplateId]
  );

  return (
    <ExpertJourneyPage helmetKey="pageTitles.expertJourney.worksheet.show" journeyId={journeyId}>
      <HandleQuery query={getJourneyWithPlanQuery}>
        {_journeyData => (
          <HandleQuery query={getWorksheetTemplateQuery}>
            {data => (
              <WorksheetEditor
                initialWorksheet={data.worksheetTemplate}
                journeyId={journeyId}
                loading={loading}
                onSave={onSave}
              />
            )}
          </HandleQuery>
        )}
      </HandleQuery>
    </ExpertJourneyPage>
  );
};

export default ExpertJourneyWorksheetTemplatePage;
